import { reportError } from '@ms/yammer-telemetry';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

import type { GlobalCopilotEntrypoint } from '../telemetry/types';

import type { GlobalCopilotInstance } from './globalCopilotInstanceStore';
import { useGlobalCopilotInstanceStore } from './globalCopilotInstanceStore';
import type { GlobalCopilotContext } from './types';

export interface GlobalCopilotState {
  /*
   * Is Global Copilot enabled for the current application.
   */
  readonly isEnabled: boolean;

  /*
   * Whether the Global Copilot is open.
   */
  readonly isOpen: boolean;

  /*
   * Entrypoint used to open the Global Copilot.
   */
  readonly entrypoint: GlobalCopilotEntrypoint | undefined;

  /*
   * The session ID for the Global Copilot.
   * A new session ID is generated each time the Global Copilot is opened.
   */
  readonly sessionId: string;

  /*
   * Opens the Global Copilot. Does nothing if the Global Copilot is already open.
   * @returns A promise that resolves to the current Global Copilot instance once it is loaded and ready. Rejects if the instance is null.
   */
  open(): Promise<Exclude<GlobalCopilotInstance, null>>;

  /* * Opens the Global Copilot with the specified entrypoint.
   * @param entrypoint The entrypoint to use when opening the Global Copilot.
   * @returns A promise that resolves to the current Global Copilot instance once it is loaded and ready. Rejects if the instance is null.
   */
  openFromEntrypoint(entrypoint: GlobalCopilotEntrypoint): ReturnType<GlobalCopilotState['open']>;

  /*
   * Closes the Global Copilot.
   */
  close(): void;

  /*
   * The session operation context data used in the Global Copilot.
   */
  readonly sessionOperationContextData: GlobalCopilotContext | null;

  /*
   * Replaces the session operation context data used in the Global Copilot.
   */
  replaceSessionOperationContextData(sessionOperationContextData: GlobalCopilotContext): void;

  /*
   * Clears the session operation context data used in the Global Copilot.
   */
  clearSessionOperationContextData(): void;

  /*
   * Whether to use the session operation context data in the Global Copilot.
   */
  readonly isUsingContext: boolean;

  /*
   * Whether the history button should be disabled (e.g., when streaming messages, saving chat history).
   */
  readonly isHistoryButtonDisabled: boolean;

  /*
   * Sets whether the history button should be disabled.
   */
  setIsHistoryButtonDisabled(isHistoryButtonDisabled: boolean): void;
}

export const useGlobalCopilotStore = create<GlobalCopilotState>((set, get) => {
  const open = (): Pick<GlobalCopilotState, 'isOpen' | 'sessionId'> => ({
    isOpen: true,
    sessionId: uuidv4(),
  });
  const close = (): Pick<GlobalCopilotState, 'isOpen' | 'entrypoint' | 'isHistoryButtonDisabled'> => {
    useGlobalCopilotInstanceStore.getState().resetInstancePromise();

    return { isOpen: false, entrypoint: undefined, isHistoryButtonDisabled: false };
  };

  return {
    isEnabled: false,
    isOpen: false,
    entrypoint: undefined,
    sessionId: uuidv4(),
    open: async () => {
      set(open);

      const instance = await useGlobalCopilotInstanceStore.getState().instancePromise;

      if (instance == null) {
        reportError({
          error: new Error('GlobalCopilotInitFailed'),
          eventProperties: {
            errorCode: 'GlobalCopilotInitFailed',
          },
        });

        return Promise.reject(new Error('GlobalCopilotInitFailed'));
      }

      return instance;
    },
    openFromEntrypoint: async (entrypoint) => {
      set({ entrypoint, ...open() });

      return get().open();
    },
    close: () => set(close),
    sessionOperationContextData: null,
    clearSessionOperationContextData: () => set({ sessionOperationContextData: null, isUsingContext: false }),
    replaceSessionOperationContextData: (sessionOperationContextData) =>
      set({
        sessionOperationContextData,
        isUsingContext: true,
      }),
    isUsingContext: false,
    isHistoryButtonDisabled: false,
    setIsHistoryButtonDisabled: (isHistoryButtonDisabled) => set({ isHistoryButtonDisabled }),
  };
});

export const enableGlobalCopilot = () => useGlobalCopilotStore.setState({ isEnabled: true });
